import React from 'react';
import useReveal from '../../hooks/useReveal';

const TermsOfUse = () => {
  document.title = 'Universal Terms of Use (UToU) - Cloudvantage';
  const [headingRef, headingVisible] = useReveal();
  return (
    <div className="max-w-4xl mx-auto py-10 px-4 md:px-6 text-black dark:text-white">
      <h1 ref={headingRef} className={`text-xl font-bold mb-6 ${headingVisible ? 'animate-reveal' : 'opacity-0'}`}>Universal Terms of Use (UToU)</h1>

      <section className="mb-8">
        <p className="text-l font-semibold mb-4">
          IMPORTANT: THESE TERMS AND CONDITIONS CONTAIN A CLASS ACTION WAIVER.
          DEPENDING ON THE JURISDICTION, THAT SECTION MAY NOT APPLY.
        </p>

        <p className="mb-4">
          If you are attempting to access the Cloudvantage Cloud Services as a
          customer of a Cloudvantage Cloud reseller, the terms below do not
          apply to you, and your agreement with your reseller governs your use
          of the Cloudvantage Cloud Services.
        </p>

        <p className="mb-4">
          If you signed an Order Form directly with Cloudvantage for
          Cloudvantage Cloud Services, additional terms apply to you as stated
          in the Order Form, and the additional terms and conditions listed in
          the Order Form govern your use of the Cloudvantage Cloud Services.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">
          1. Terms and Conditions and Related Agreement and Policies;
          Modifications.
        </h2>
        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            These online Cloudvantage Universal Terms of Use are an agreement
            which sets forth the terms and conditions (collectively, the “UToU”)
            governing access to and the use of the products, services, website
            (https://cloudvantage.com.ng) and/or mobile-optimized versions of
            the Website to which the UToU are linked (collectively, the
            “Website” and/or “Products”), all of which are owned or operated by
            Company, its parent, subsidiaries, affiliates and/or brands, by a
            Website visitor, user and/or the party set forth in the related
            Company order form (“User”), which order is incorporated herein by
            this reference (together with any subsequent order forms submitted
            by User to Company, collectively, the “Order”).
          </li>
          <li>
            The term “Website” also includes and is not limited to any
            subdomains of the Website and any content, code, data, services,
            products, features or functionality made available from or through
            the Website.
          </li>
          <li>
            Company and User may be individually referred to herein as a “Party”
            and together the “Parties.”
          </li>
          <li>
            The UToU are effective on the date the User first accessed the
            Website or the date the Company signed the Order, whichever is
            earlier.
          </li>
          <li>
            User signifies that User has read, understands, acknowledges and
            agrees to be bound by the UToU, along with Company’s{' '}
            <a
              href="/acceptable-use-policy"
              target="_blank"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              Acceptable Use Policy
            </a>{' '}
            (“AUP”) and{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              Privacy Policy
            </a>{' '}
            (“
            <a
              href="/privacy-policy"
              target="_blank"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              Privacy Policy
            </a>
            ”), and Order (if applicable), all of which are incorporated herein
            by reference. The UToU,{' '}
            <a
              href="/acceptable-use-policy"
              target="_blank"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              AUP
            </a>
            ,{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              Privacy Policy
            </a>{' '}
            and Order (if applicable) shall collectively be referred to as the
            “Agreements.” In case of a conﬂict between an Order or term within
            the{' '}
            <a
              href="/acceptable-use-policy"
              target="_blank"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              AUP
            </a>
            ,{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              Privacy Policy
            </a>{' '}
            or UToU, the terms of the UToU shall control. Unless otherwise set
            forth in the Order or the UToU,{' '}
            <a
              href="/acceptable-use-policy"
              target="_blank"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              AUP
            </a>{' '}
            or{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              className="text-blue-600 hover:text-blue-800 no-underline"
            >
              Privacy Policy
            </a>
            , the capitalized terms contained within those documents carry the
            same meanings whenever used in the UToU.
          </li>
          <li>
            The Website and Agreements may be modified at any time by Company
            upon posting to the Website. Any such modifications shall be
            effective immediately. A User’s access and/or use of the Website
            after any modifications to the Website or Agreements have been made
            shall constitute such party’s acceptance of each as of the “Last
            Updated Date” stated above.
          </li>
        </ol>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">2. Ownership</h2>
        <p className="mb-4">
          Unless otherwise stated, all content included on the Website is and
          shall continue to be the property of Company or its content suppliers
          and is protected under applicable copyright, patent, trademark, and
          other proprietary rights. Any copying, redistribution, use or
          publication of any such content or any part of the Website is
          prohibited, except as expressly permitted in the Agreements. Under no
          circumstances will User acquire any ownership rights or other interest
          in any content by or through use of this Website. The term
          CLOUDVANTAGE and the CLOUDVANTAGE logo, and others are either
          trademarks or registered trademarks of Company. Other product and
          company names mentioned on the Website may be trademarks of their
          respective owners.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">3. Intended Audience</h2>
        <p className="mb-4">
          The Website and Subscriptions are intended for adults only. The
          Website and Subscriptions are not intended for any individual under
          the age of eighteen (18).
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">4. Website Use</h2>
        <p className="mb-4">
          Company grants User a limited, revocable, nonexclusive license to use
          the Website solely for User’s own personal use and not for
          republication, distribution, assignment, sublicense, sale, preparation
          of derivative works, or other use. User agrees not to copy materials
          on the Website, reverse engineer or break into the Website, or use
          materials, products or services in violation of any law. The use of
          the Website is at the discretion of Company and Company may terminate
          User’s use of the Website at any time.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">5. Compliance with Laws</h2>
        <p className="mb-4">
          User agrees to comply with all applicable laws regarding User’s use of
          the Website. User further agrees that information provided by User to
          Company is truthful and accurate to the best of User’s knowledge.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">
          6. Data Privacy and Protection
        </h2>
        <p className="mb-4">
          Access and use of the Website involve the processing of personal data
          about visitors, users and/or User. Company’s{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Privacy Policy
          </a>{' '}
          and{' '}
          <a
            href="/data-processing"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Data Processing Addendum
          </a>{' '}
          (“DPA”), which is hereby incorporated by this reference, as
          applicable, relate to personal data and/or personal information,
          provide User contractual assurance that Company has implemented
          mechanisms to ensure the processing of such data and information,
          including transfers of personal data from the European Economic Area
          to a third country, and complies under applicable data privacy laws.
          Company reserves the right, and User authorizes Company to use and
          assign all information regarding User’s Website use and all
          information provided by User in any manner consistent with the{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Privacy Policy
          </a>
          . For the purposes of the DPA and the Standard Contractual Clauses
          attached to the DPA (when and as applicable) Users (and applicable
          affiliates) are considered the Data Controller or Data Exporter.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">
          7. Conduct Towards Company
        </h2>
        <p className="mb-4">
          Company will protect the health, safety, and welfare of Company’s
          employees. Unprofessional conduct, threats, abusive language
          (including, but not limited to, disparaging remarks regarding the sex,
          race, religion of Company’s employees) and/or anything that could be
          considered hate speech in the course of User’s communications with
          Company will not be tolerated. Such conduct may result in the
          termination of communications and repeated offenses may result in the
          suspension, cancellation or termination of access and use of the
          Website and cancellation of an Order (if applicable, and in such
          instance without any refund to User, as determined in Company’s sole
          and absolute discretion).
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">
          8. Trademark and/or Copyright Claims
        </h2>
        <p className="mb-4">
          Company supports the protection of intellectual property. If User
          would like to submit a trademark or copyright claim, please discuss
          with the Company.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">
          9. Links To Third-Party Websites
        </h2>
        <p className="mb-4">
          The Websites may contain links to third-party websites that are not
          owned or controlled by Company. Company assumes no responsibility for
          such content, terms, and conditions, privacy policies, or practices of
          any third-party websites. In addition, Company does not censor or edit
          the content of any third-party website. By using the Websites, User
          expressly releases Company from any and all liability arising from
          User use of any third-party website. Accordingly, Company encourages
          User to be aware when User leaves the Website to review the terms and
          conditions, privacy policies, and other governing documents of each
          other website that User may visit.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">10. Feedback</h2>
        <p className="mb-4">
          User understands and agrees that Feedback (as defined below) provided
          by User shall not be treated as confidential information (unless
          Company so agrees in writing), and nothing in the UToU or in the
          Parties’ dealings arising out of or related to the UToU will restrict
          Company’s right to use, profit from, disclose, publish, keep secret,
          or otherwise exploit Feedback, without compensating or crediting User.
          “Feedback” refers to any suggestion or idea for improving or otherwise
          modifying the Subscriptions, Documentation and Specifications and/or
          any of Company’s products or services.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">
          11. Disclaimer of Representations and Warranties
        </h2>
        <p className="mb-4">
          THE INFORMATION ON THE WEBSITE IS PROVIDED ON AN “AS IS,” “AS
          AVAILABLE” BASIS. USER AGREES THAT USE OF THE WEBSITE IS AT USER’S
          SOLE RISK. COMPANY DISCLAIMS ALL WARRANTIES OF ANY KIND, INCLUDING BUT
          NOT LIMITED TO ANY EXPRESS WARRANTIES, STATUTORY WARRANTIES, AND ANY
          IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, AND NON-INFRINGEMENT. TO THE EXTENT USER’S JURISDICTION DOES
          NOT ALLOW LIMITATIONS ON WARRANTIES, THIS LIMITATION MAY NOT APPLY TO
          USER. USER’S SOLE AND EXCLUSIVE REMEDY RELATING TO USER’S USE OF THE
          WEBSITE SHALL BE TO DISCONTINUE USING THE WEBSITE.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">
          12. LIMITATION OF LIABILITY
        </h2>
        <p className="mb-4">
          UNDER NO CIRCUMSTANCES WILL COMPANY BE LIABLE OR RESPONSIBLE FOR ANY
          DIRECT, INDIRECT, INCIDENTAL, CONSEQUENTIAL (INCLUDING DAMAGES FROM
          LOSS OF BUSINESS, LOST PROFITS, LITIGATION, OR THE LIKE), SPECIAL,
          EXEMPLARY, PUNITIVE, OR OTHER DAMAGES, UNDER ANY LEGAL THEORY, ARISING
          OUT OF OR IN ANY WAY RELATING TO THE WEBSITE, USER’S USE OF THE
          WEBSITE, OR THE CONTENT, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH
          DAMAGES. USER’S SOLE REMEDY FOR DISSATISFACTION WITH THE WEBSITE
          AND/OR CONTENT IS TO CEASE USE OF THE WEBSITE. User may have
          additional rights under certain laws (including consumer laws) which
          do not allow the exclusion of implied warranties, or the exclusion or
          limitation of certain damages. If these laws apply to User, the
          exclusions or limitations in the Agreements that directly conflict
          with such laws may not apply to User.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">13. Indemnification</h2>
        <p className="mb-4">
          User agrees to indemnify, defend and hold Company and its partners,
          employees, and affiliates, harmless from any liability, loss, claim
          and expense, including reasonable attorney’s fees, related to User’s
          violation of the Agreements or use of the Website.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">
          14. Trial and Class Action Waiver
        </h2>
        <p className="mb-4">
          PLEASE READ THIS CAREFULLY IF USER IS A RESIDENT OF THE UNITED STATES
          OF AMERICA. IT AFFECTS USER’S LEGAL RIGHTS
          <ol className="list-decimal pl-6 mt-4 mb-6">
            <li>
              <strong>Waiver of Jury Trial.</strong> USER HEREBY WAIVE USER’S
              CONSTITUTIONAL AND STATUTORY RIGHTS TO GO TO COURT AND HAVE A
              TRIAL IN FRONT OF A JUDGE OR A JURY. In the event any litigation
              should arise between the Parties in any state or federal court in
              a lawsuit, USER WAIVES ALL RIGHTS TO A JURY TRIAL.
            </li>
            <li>
              <strong>Waiver of Class or Consolidated Actions.</strong> USER
              UNDERSTANDS AND AGREES ALL CLAIMS AND DISPUTES WHICH MAY BE MADE
              WITHIN THE SCOPE OF THE AGREEMENTS MUST BE LITIGATED ON AN
              INDIVIDUAL BASIS AND NOT ON A CLASS BASIS, AND CLAIMS OF MORE THAN
              ONE USER CANNOT BE LITIGATED JOINTLY OR CONSOLIDATED WITH THOSE OF
              ANY OTHER USER. USER UNDERSTANDS AND AGREES THAT, BY ENTERING INTO
              THE AGREEMENTS, THE PARTIES ARE EACH WAIVING TO PARTICIPATE IN A
              CLASS ACTION.
            </li>
          </ol>
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">15. General Terms</h2>
        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            <strong>Heading:</strong> The headings herein are for convenience
            only and are not part of the Agreements.
          </li>
          <li>
            <strong>Limitation of Actions:</strong> No action, regardless of
            form, arising by reason of or in connection with the Agreements may
            be brought by either party more than one (1) year after the cause of
            action has arisen.
          </li>
          <li>
            <strong>Force Majeure:</strong> No delay, failure, or default, other
            than a failure to pay fees when due, will constitute a breach of the
            UToU to the extent caused by acts of war, terrorism, hurricanes,
            earthquakes, other acts of God or of nature, strikes or other labour
            disputes, riots or other acts of civil disorder, embargoes,
            epidemics, pandemics, or other causes beyond the performing party’s
            reasonable control.
          </li>
          <li>
            <strong>Severability:</strong> To the extent permitted by applicable
            law, the Parties hereby waive any provision of law that would render
            any clause of the UToU invalid or otherwise unenforceable in any
            respect. In the event that a provision of the UToU is held to be
            invalid or otherwise unenforceable, such provision will be
            interpreted to fulfill its intended purpose to the maximum extent
            permitted by applicable law, and the remaining provisions of the
            UToU will continue in full force and effect.
          </li>
          <li>
            <strong>No Waiver:</strong> Neither Party will be deemed to have
            waived any of its rights under the UToU by lapse of time or by any
            statement or representation other than by an authorized
            representative in an explicit written waiver. No waiver of a breach
            of the UToU will constitute a waiver of any other breach of the
            UToU.
          </li>
          <li>
            <strong>Choice of Law and Jurisdiction:</strong> The UToU will be
            governed solely by Nigerian laws, without reference to: (a) any
            conflicts of law principle that would apply the substantive laws of
            another jurisdiction to the Parties’ rights or duties; (b) the 1980
            United Nations Convention on Contracts for the International Sale of
            Goods; or (c) other international laws. The Parties consent to the
            jurisdiction of the Nigerian courts.
          </li>
          <li>
            <strong>Attorney’s Fees and Costs:</strong> Claims for attorney’s
            fees and other costs (including expert fees) are recoverable by the
            prevailing party in any legal action associated with the enforcement
            of the UToU, or the collection of any amounts due under the UToU.
          </li>
          <li>
            <strong>Entire Agreement; Amendments:</strong> Unless otherwise
            stated in the Order (if applicable), the Agreements and documents
            incorporated herein by reference, supersedes all prior discussions,
            negotiations, and agreements between the Parties with respect to the
            subject matter hereof, and the Agreements (unless specifically
            stated therein) constitute the sole and entire agreement between the
            Parties with respect to the matters covered hereby. No additional
            terms or conditions relating to the subject matter of the Agreements
            shall be effective unless approved in writing by an authorized
            representative of User and Company.
          </li>
          <li>
            <strong>Translation:</strong> The Agreements are written in English.
            Company may, but is not obligated to, translate the terms into other
            languages. To the extent any translation is provided, it is provided
            for convenience purposes only, and in the event of a conflict
            between a translated version of the Agreements and the English, the
            English version will control. Where a translated version is required
            to be provided by law such is to be considered binding in which both
            language versions shall have equal validity; User acknowledges that
            User has reviewed both language versions and each is substantially
            the same in all material respects; and in the event of any
            discrepancy between any versions, the translated version may
            prevail.
          </li>
          <li>
            <strong>Termination:</strong> Subject to an Order (if applicable),
            Company may terminate the Agreements at any time, with or without
            notice, for any reason.
          </li>
          <li>
            <strong>Company Contact Information:</strong> If User has any
            questions about the Agreements, please contact Company by email at
            privacy@cloudvantage.com.ng.
          </li>
        </ol>
      </section>
    </div>
  );
};

export default TermsOfUse;
